import { inject, Injectable } from '@angular/core';
import { AngularFireAuth } from '@angular/fire/compat/auth';
import { Data } from '@angular/router';
import { CommonVariable } from 'src/app/app.constants';
import { GTMSagmentService } from 'src/app/services/gtm-sagment.service';
import { PaymentService } from 'src/app/services/payment.service';
import { take, map, filter, timeout } from 'rxjs/operators';
import { of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class CheckoutComService {
  private paymentService = inject(PaymentService);
  private GTMService = inject(GTMSagmentService);
  private angularFireAuth = inject(AngularFireAuth);

  private retryCount = 0;
  private maxRetry = 6;
  private retryTimerId: ReturnType<typeof setTimeout> | null = null;
  private frameActivationTimerId: ReturnType<typeof setTimeout> | null = null;
  private frameActivated = false;

  initializeFrames(config: {
    Frames: Data,
    element: string;
    publicKey: string;
    language: string;
    onValidationChanged: (event) => void;
    onCardTokenized: (event) => void;
    onTokenizationFailed: () => void;
    onCardValidationChanged: () => void;
    onError: (message: string) => void;
  }) {
    this.cancelRetries();
    this.retryCount = 0;
    this.frameActivated = false;
    this.attemptInitialization(config);
  }

  private registerFrameActivatedHandler(frames: Data): void {
    if (!frames?.Events?.FRAME_ACTIVATED || typeof frames.addEventHandler !== 'function') {
      return;
    }
    frames.addEventHandler(frames.Events.FRAME_ACTIVATED, () => {
      this.frameActivated = true;
      this.retryCount = 0;
      if (this.frameActivationTimerId) {
        clearTimeout(this.frameActivationTimerId);
        this.frameActivationTimerId = null;
      }
    });
  }
  private attemptInitialization(config) {
    const frames = (typeof window !== 'undefined' && (window as Data).Frames) || config.Frames;
 
    if (!this.isFramesApiValid(frames)) {
      return this.retry(config);
    }
 
    // Keep the config in sync so callbacks/onSubmit use the resolved instance.
    config.Frames = frames;
 
    let element: Element | null;
    try {
      element = document.querySelector(config.element);
    } catch (e) {
      return this.retry(config);
    }
 
    if (!element) {
      return this.retry(config);
    }
 
    try {
      this.removeHandlers(frames);
 
      frames.init({
        publicKey: config.publicKey,
        localization: config.language,
        frameSelector: config.element,
      });
 
      frames.addEventHandler(
        frames.Events.CARD_VALIDATION_CHANGED,
        config.onCardValidationChanged
      );
 
      frames.addEventHandler(
        frames.Events.FRAME_VALIDATION_CHANGED,
        config.onValidationChanged
      );
 
      frames.addEventHandler(
        frames.Events.CARD_TOKENIZED,
        config.onCardTokenized
      );
 
      frames.addEventHandler(
        frames.Events.CARD_TOKENIZATION_FAILED,
        config.onTokenizationFailed
      );

      this.registerFrameActivatedHandler(frames);

      this.frameActivationTimerId = setTimeout(() => {
        this.frameActivationTimerId = null;

        if (this.isFrameActivated(config.element)) {
          this.retryCount = 0;
          return;
        }

        this.resetFrameContainer(config.element);
        this.retry(config);
      }, 8000);
      return;
 
    } catch (error) {
      if (this.isFrameActivated(config.element)) {
        try {
          frames.addEventHandler(
            frames.Events.CARD_VALIDATION_CHANGED,
            config.onCardValidationChanged
          );
          frames.addEventHandler(
            frames.Events.FRAME_VALIDATION_CHANGED,
            config.onValidationChanged
          );
          frames.addEventHandler(
            frames.Events.CARD_TOKENIZED,
            config.onCardTokenized
          );
          frames.addEventHandler(
            frames.Events.CARD_TOKENIZATION_FAILED,
            config.onTokenizationFailed
          );
          this.registerFrameActivatedHandler(frames);
        } catch (_handlerError) {
          // Handler registration failed even though the frame is activated — carry on.
        }
        return;
      }

      this.resetFrameContainer(config.element);
      this.retry(config);
    }
  }

  
  handleCheckoutComSuccessPayment(
    payment_response: Data,
    purchaseDetail: Data,
    altprosubDetails: Data,
    onComplete: (args) => void,
    customerPurchaseId?: string,
    paymentRequest?: Data
  ) {
    const pushPaymentSuccess = (cpId: string, request?: Data) => {
      if (!cpId) {
        return;
      }
      const paidRequest = request || paymentRequest || {};
      const offers = purchaseDetail?.offers ?? altprosubDetails ?? {};
      const preferredCurrency =
        paidRequest?.currency ||
        offers?.currency ||
        localStorage.getItem('user_currency') ||
        'GBP';
      // Prefer values already written to checkoutcom/{logId}.payment_request —
      // host offer/price objects are often currency maps or use `offerid` only.
      const amount =
        paidRequest?.amount ??
        paidRequest?.preferred_currency?.Value ??
        paidRequest?.value ??
        altprosubDetails?.price ??
        purchaseDetail?.prize ??
        offers?.price ??
        offers?.int_price?.[preferredCurrency] ??
        offers?.int_price?.GBP ??
        offers?.int_price?.gbp;
      const offerId =
        paidRequest?.offer_id ??
        altprosubDetails?.offerid ??
        altprosubDetails?.offer_id ??
        purchaseDetail?.offer_id ??
        offers?.offerid ??
        offers?.offer_id;
      const prosubId =
        paidRequest?.prosub_id ??
        altprosubDetails?.prosub_id ??
        purchaseDetail?.prosub_id ??
        localStorage.getItem('prosub_id');
      this.GTMService.GTM_CJ_conversion(
        amount,
        preferredCurrency,
        offerId,
        this.GTMService?.userIsNew ? 'new' : 'return',
        CommonVariable.countryCode,
        purchaseDetail?.p_type ? purchaseDetail?.p_type : '10',
        prosubId,
        cpId
      );
    };

    const resolveCpIdFromDoc = (paymentResData: Data): string => {
      const purchase = paymentResData?.payment_request?.customer_purchase;
      // Prefer plain customer_purchase_id for AppLovin transaction_id.
      return String(
        purchase?.customer_purchase_id ||
          purchase?.encrypted_customer_purchase_id ||
          ''
      );
    };

    const immediateCpId = String(
      customerPurchaseId ||
        payment_response?.customer_purchase_id ||
        payment_response?.encrypted_customer_purchase_id ||
        ''
    );

    if (immediateCpId) {
      pushPaymentSuccess(immediateCpId, paymentRequest);
    } else {
      // Wait for checkout doc purchase id + request fields. Never push empty —
      // that creates AppLovin Purchase with null transaction_id / item_id / value 0.
      this.paymentService.getCheckOutComResponse$
        .pipe(
          map((paymentResData: Data) => ({
            cpId: resolveCpIdFromDoc(paymentResData),
            paymentRequest: paymentResData?.payment_request,
          })),
          filter((resolved) => !!resolved.cpId),
          take(1),
          timeout({ first: 8000, with: () => of({ cpId: '', paymentRequest: undefined }) })
        )
        .subscribe({
          next: (resolved) => {
            if (resolved?.cpId) {
              pushPaymentSuccess(String(resolved.cpId), resolved.paymentRequest);
            }
          },
          error: () => {
            // Skip rather than emit an empty purchase payload.
          },
        });
    }

    this.angularFireAuth.currentUser.then((user) => {
      onComplete(user);
    });
  }

  // Helpers

  // used by this service so partial loads do not fall silently into the retry loop.
  private isFramesApiValid(frames: unknown): boolean {
    if (!frames || typeof frames !== 'object') return false;
    const f = frames as Record<string, unknown>;
    return (
      typeof f['init'] === 'function' &&
      typeof f['addEventHandler'] === 'function' &&
      typeof f['removeAllEventHandlers'] === 'function' &&
      f['Events'] !== null &&
      typeof f['Events'] === 'object'
    );
  }

  private retry(config) {

    if (this.retryCount >= this.maxRetry) {
      config.onError("Failed to load payment form");
      return;
    }

    this.retryCount++;
    const delay = Math.min(1000 * this.retryCount, 3000);

    if (this.retryTimerId) clearTimeout(this.retryTimerId);
  this.retryTimerId = setTimeout(() => {
    this.retryTimerId = null;
    this.attemptInitialization(config);
  }, delay);
  }

  private removeHandlers(Frames: Data) {

    if (!Frames?.removeAllEventHandlers) return;

    Frames.removeAllEventHandlers(
      Frames.Events.CARD_VALIDATION_CHANGED
    );

    Frames.removeAllEventHandlers(
      Frames.Events.FRAME_VALIDATION_CHANGED
    );

    Frames.removeAllEventHandlers(
      Frames.Events.CARD_TOKENIZED
    );

    Frames.removeAllEventHandlers(
      Frames.Events.CARD_TOKENIZATION_FAILED
    );

    if (Frames.Events.FRAME_ACTIVATED) {
      Frames.removeAllEventHandlers(Frames.Events.FRAME_ACTIVATED);
    }
  }

  cancelRetries(): void {
    if (this.retryTimerId) {
      clearTimeout(this.retryTimerId);
      this.retryTimerId = null;
    }
    if (this.frameActivationTimerId) {
      clearTimeout(this.frameActivationTimerId);
      this.frameActivationTimerId = null;
    }
    this.retryCount = 0;
  }

  // (e.g. during SSR teardown or test cleanup) all pending timers are cancelled
  // and callbacks do not fire against a torn-down UI.
  ngOnDestroy(): void {
    this.cancelRetries();
  }

  //Shared helper so querySelector errors never escape a try/catch boundary,
  // regardless of call site.
  private safeQuerySelector(selector: string): Element | null {
    try {
      return document.querySelector(selector);
    } catch {
      return null;
    }
  }

  private hasHostedCheckoutFrame(selector: string): boolean {
    const el = this.safeQuerySelector(selector);
    const iframe = el?.querySelector('iframe') as HTMLIFrameElement | null;
    return !!iframe;
  }

  private isFrameActivated(selector: string): boolean {

    if (this.frameActivated) {
      return true;
    }

    const el = this.safeQuerySelector(selector);
    if (!el) {
      return false;
    }

    // Treat the frame as activated once Checkout.com flags it OR its hosted
    // iframe has been injected. This prevents the watchdog from destroying a
    // frame that is simply slow to finish loading (e.g. after the tab was idle),
    // while still retrying when init produced no iframe at all.
    return (
      !!el.classList?.contains('frame--activated') ||
      this.hasHostedCheckoutFrame(selector)
    );
  }

  private resetFrameContainer(selector: string): void {
    const el = this.safeQuerySelector(selector);
    if (!el) return;

    el.classList.remove(
      'frame--activated',
      'frame--rendered',
      'frame--focus',
      'frame--valid',
      'frame--invalid'
    );

    while (el.firstChild) {
      el.removeChild(el.firstChild);
    }
  }
}